import { useState } from 'react'
import { CircleCheck, TriangleAlert } from 'lucide-react'
import { createAdapter } from '@/lib/printing/adapters'
import { PRINTER_OPTIONS } from '@/lib/printing/printerOptions'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/primitives'
import { usePrinter } from '@/stores/printer'
import { t } from '@/i18n'

export function PrinterSection() {
  const printer = usePrinter()
  const [testResult, setTestResult] = useState<string | null>(null)

  async function testDrawer() {
    setTestResult(null)
    try {
      await printer.adapter.openDrawer?.()
      setTestResult('Sent the drawer pulse.')
    } catch (caught) {
      setTestResult(caught instanceof Error ? caught.message : 'Could not reach the printer.')
    }
  }

  return (
    <div>
      <p className="mb-4 text-sm text-[var(--text-secondary)]">
        Chosen per device and remembered here, because the same person moves between a phone with
        a Bluetooth printer and a PC with a USB one.
      </p>

      <ul className="flex flex-col gap-2">
        {PRINTER_OPTIONS.map((option) => {
          const adapter = createAdapter(option.kind)
          const supported = adapter.isSupported()
          const selected = printer.kind === option.kind
          const Icon = option.icon

          return (
            <li key={option.kind}>
              <button
                type="button"
                onClick={() => printer.choose(option.kind)}
                disabled={!supported}
                className={`surface-raised flex w-full items-start gap-3 rounded-[var(--radius-card)] border p-4 text-left disabled:opacity-50 ${
                  selected ? 'border-[var(--accent)] ring-1 ring-[var(--accent)]' : ''
                }`}
              >
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-secondary)]" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{option.title}</span>
                    {selected && <Badge tone="accent">{t('In use')}</Badge>}
                    {option.drawer && <Badge tone="neutral">{t('Opens drawer')}</Badge>}
                    {!supported && <Badge tone="danger">{t('Not available here')}</Badge>}
                  </div>
                  <p className="mt-1 text-sm text-[var(--text-secondary)]">{option.detail}</p>
                </div>
                {selected && <CircleCheck className="h-5 w-5 shrink-0 text-[var(--accent)]" />}
              </button>
            </li>
          )
        })}
      </ul>

      {(printer.kind === 'bluetooth' || printer.kind === 'webusb') && (
        <div className="surface-raised mt-4 rounded-[var(--radius-card)] border p-4">
          <p className="text-sm font-medium">{t('Pair the printer')}</p>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            The browser asks which device to use. Pairing has to start from a button press, so it
            cannot be done automatically.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => printer.connect()} disabled={printer.isConnecting}>
              {printer.isConnecting ? 'Connecting…' : printer.adapter.isReady() ? 'Reconnect' : 'Connect'}
            </Button>
            <Button variant="secondary" onClick={testDrawer}>
              {t('Test drawer')}
            </Button>
          </div>

          {printer.error && (
            <p className="mt-3 flex items-start gap-2 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              {printer.error}
            </p>
          )}
          {testResult && (
            <p className="mt-3 rounded-lg surface-sunken px-3 py-2 text-sm">{testResult}</p>
          )}
        </div>
      )}
    </div>
  )
}
